import { getActiveOrgId } from '@/stores/orgStore';
import { apiFetch } from '@/lib/api';

export class OrgKnowledgeService {
  private orgId() {
    const id = getActiveOrgId();
    if (!id) throw new Error('No active organization');
    return id;
  }

  private headers() {
    const id = this.orgId();
    return { 'Content-Type': 'application/json', 'X-Organization-Id': id } as Record<string, string>;
  }

  async getProfile() {
    const id = this.orgId();
    const res = await apiFetch(`/api/orgs/${id}/profile`, { headers: this.headers() });
    if (!res.ok) throw new Error('Failed to load profile');
    const payload = await res.json();
    return payload?.data || {};
  }

  async saveProfile(profile: any) {
    const id = this.orgId();
    const res = await apiFetch(`/api/orgs/${id}/profile`, {
      method: 'PUT',
      headers: this.headers(),
      body: JSON.stringify(profile),
    });
    if (!res.ok) throw new Error('Failed to save profile');
  }

  async listAudiences() {
    const id = this.orgId();
    const res = await apiFetch(`/api/orgs/${id}/audiences`, { headers: this.headers() });
    if (!res.ok) throw new Error('Failed to load audiences');
    const payload = await res.json();
    return payload?.data || [];
  }

  async upsertAudience(audience: any) {
    const id = this.orgId();
    const res = await apiFetch(`/api/orgs/${id}/audiences`, {
      method: 'POST',
      headers: this.headers(),
      body: JSON.stringify(audience),
    });
    if (!res.ok) throw new Error('Failed to save audience');
  }

  async deleteAudience(audienceId: string) {
    const id = this.orgId();
    const res = await apiFetch(`/api/orgs/${id}/audiences/${audienceId}`, { method: 'DELETE', headers: this.headers() });
    if (!res.ok) throw new Error('Failed to delete audience');
  }

  // Objectives removed in Milestone 4

  async listSources() {
    const id = this.orgId();
    const res = await apiFetch(`/api/orgs/${id}/knowledge/sources`, { headers: this.headers() });
    if (!res.ok) throw new Error('Failed to load sources');
    const payload = await res.json();
    return payload?.data || [];
  }

  async upsertSource(source: any) {
    const id = this.orgId();
    const res = await apiFetch(`/api/orgs/${id}/knowledge/sources`, {
      method: 'POST',
      headers: this.headers(),
      body: JSON.stringify(source),
    });
    if (!res.ok) throw new Error('Failed to save source');
  }

  async deleteSource(sourceId: string) {
    const id = this.orgId();
    const res = await apiFetch(`/api/orgs/${id}/knowledge/sources/${sourceId}`, { method: 'DELETE', headers: this.headers() });
    if (!res.ok) throw new Error('Failed to delete source');
  }

  async listDocs() {
    const id = this.orgId();
    const res = await apiFetch(`/api/orgs/${id}/knowledge/docs`, { headers: this.headers() });
    if (!res.ok) throw new Error('Failed to load docs');
    const payload = await res.json();
    return payload?.data || [];
  }

  async upsertDoc(doc: any) {
    const id = this.orgId();
    const res = await apiFetch(`/api/orgs/${id}/knowledge/docs`, {
      method: 'POST',
      headers: this.headers(),
      body: JSON.stringify(doc),
    });
    if (!res.ok) throw new Error('Failed to save doc');
  }

  async deleteDoc(docId: string) {
    const id = this.orgId();
    const res = await apiFetch(`/api/orgs/${id}/knowledge/docs/${docId}`, { method: 'DELETE', headers: this.headers() });
    if (!res.ok) throw new Error('Failed to delete doc');
  }

  // M0.2: R2 upload flow
  async presignDocUpload(filename: string, contentType: string): Promise<{ url: string; r2Key: string; docId: string }> {
    const id = this.orgId();
    const res = await apiFetch(`/api/orgs/${id}/knowledge/docs/presign`, {
      method: 'POST',
      headers: this.headers(),
      body: JSON.stringify({ filename, contentType }),
    });
    if (!res.ok) throw new Error('Failed to presign upload');
    const payload = await res.json();
    return payload?.data;
  }

  async uploadToPresignedUrl(url: string, file: Blob, contentType: string): Promise<void> {
    const res = await fetch(url, { method: 'PUT', body: file, headers: { 'Content-Type': contentType } });
    if (!res.ok) throw new Error('Failed to upload to storage');
  }

  async finalizeDocFromR2(input: { id: string; title: string; r2Key: string; source_id?: string }): Promise<void> {
    await this.upsertDoc(input);
  }

  async searchKnowledge(q: string, topK = 8): Promise<Array<{ id: string; score: number; text: string; docId?: string }>> {
    const id = this.orgId();
    const res = await apiFetch(`/api/orgs/${id}/knowledge/search`, {
      method: 'POST',
      headers: this.headers(),
      body: JSON.stringify({ q, topK }),
    });
    if (!res.ok) throw new Error('Failed to search knowledge');
    const payload = await res.json();
    return payload?.data || [];
  }

  async suggestProfileFromWebsite(websiteUrl?: string): Promise<{ mission: string; vision: string; value_proposition: string; tone: string; brand_voice: string; products_and_services: string; target_audience: string; }> {
    const id = this.orgId();
    const res = await apiFetch(`/api/orgs/${id}/ai/suggest/profile`, {
      method: 'POST',
      headers: this.headers(),
      body: JSON.stringify({ website_url: websiteUrl })
    });
    if (!res.ok) {
      let message = 'Failed to suggest profile';
      try { const payload = await res.json(); message = payload?.error?.message || message; } catch {}
      throw new Error(message);
    }
    const payload = await res.json();
    return payload?.data;
  }

  async validateWebsite(websiteUrl: string): Promise<void> {
    const id = this.orgId();
    const res = await apiFetch(`/api/orgs/${id}/onboarding/validate-website`, {
      method: 'POST',
      headers: this.headers(),
      body: JSON.stringify({ website_url: websiteUrl }),
    });
    if (!res.ok) {
      let message = 'Website is not reachable';
      try { const payload = await res.json(); message = payload?.error?.message || message; } catch {}
      throw new Error(message);
    }
  }

  async beginProfileResearch(websiteUrl?: string): Promise<void> {
    const id = this.orgId();
    const res = await apiFetch(`/api/orgs/${id}/onboarding/begin-profile-research`, {
      method: 'POST',
      headers: this.headers(),
      body: JSON.stringify({ website_url: websiteUrl }),
    });
    if (!res.ok) {
      let message = 'Failed to start profile research';
      try { const payload = await res.json(); message = payload?.error?.message || message; } catch {}
      throw new Error(message);
    }
  }

  async getDocChunks(docId: string): Promise<Array<{ id: string; text: string; created_at: string }>> {
    const id = this.orgId();
    const res = await apiFetch(`/api/orgs/${id}/knowledge/docs/${docId}/chunks`, { headers: this.headers() });
    if (!res.ok) throw new Error('Failed to get chunks');
    const payload = await res.json();
    return payload?.data || [];
  }

  async reprocessDoc(docId: string): Promise<void> {
    const id = this.orgId();
    const res = await apiFetch(`/api/orgs/${id}/knowledge/docs/${docId}/reprocess`, {
      method: 'POST',
      headers: this.headers(),
    });
    if (!res.ok) {
      const payload = await res.json().catch(() => ({}));
      const msg = payload?.error?.message || 'Failed to reprocess document';
      throw new Error(msg);
    }
  }
}


